import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { useSiteTexts } from "@/components/chat/ToolIntro";
import { Markdown } from "@/components/chat/Markdown";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  MessagesSquare, FileText, Mail, Gauge, Compass, Heart, FolderOpen,
  ArrowUpRight, GraduationCap, Rocket, Sparkles, TrendingUp,
} from "lucide-react";

const VARIANTS = {
  undergraduate: {
    icon: GraduationCap,
    headline: "Discover your path",
    intro: "Explore top schools, find your ideal field, and craft a standout application from scratch.",
    priority: [
      { icon: Compass, title: "Orientation Test", desc: "Not sure what to study? Start here.", to: "/tools/orientation", tone: "orange" },
      { icon: Heart, title: "Explore Schools", desc: "Browse and save your favorites.", to: "/favorites", tone: "navy" },
      { icon: FileText, title: "Build your CV", desc: "Create your first academic CV.", to: "/tools/cv", tone: "navy" },
    ],
  },
  postgraduate: {
    icon: Rocket,
    headline: "Sharpen your strategy",
    intro: "Target the best programs, assess your competitiveness and perfect every application document.",
    priority: [
      { icon: Gauge, title: "Admission Predictor", desc: "Assess your chances at top programs.", to: "/tools/predictor", tone: "orange" },
      { icon: Mail, title: "Cover Letter", desc: "Craft a compelling motivation letter.", to: "/tools/cover-letter", tone: "navy" },
      { icon: MessagesSquare, title: "Ask your advisor", desc: "Strategy for GMAT, research fit & more.", to: "/agent", tone: "navy" },
    ],
  },
};

const ALL_TOOLS = [
  { icon: MessagesSquare, title: "AI Advisor", to: "/agent" },
  { icon: FileText, title: "CV Writer", to: "/tools/cv" },
  { icon: Mail, title: "Cover Letter", to: "/tools/cover-letter" },
  { icon: Gauge, title: "Predictor", to: "/tools/predictor" },
  { icon: Compass, title: "Orientation", to: "/tools/orientation" },
  { icon: Heart, title: "Favorites", to: "/favorites" },
];

const TOOL_KEYS = { "/agent": "advisor", "/tools/cv": "cv", "/tools/cover-letter": "cover_letter", "/tools/predictor": "predictor", "/tools/orientation": "orientation", "/favorites": "favorites" };

function PlatformIntro({ persona }) {
  const texts = useSiteTexts(persona);
  const t = texts?.platform;
  if (!t) return null;
  return (
    <div className="mb-8 rounded-3xl border border-border bg-white p-6 sm:p-7 flex gap-4" data-testid="dash-platform-intro">
      <div className="h-11 w-11 rounded-xl bg-orange/15 flex items-center justify-center shrink-0"><Sparkles className="h-5 w-5 text-orange" /></div>
      <div className="min-w-0 [&_strong]:text-navy [&_ul]:list-disc [&_ul]:pl-4 [&_li]:my-0.5">
        <h2 className="font-display font-extrabold italic text-navy text-lg mb-1">{t.title}</h2>
        <Markdown className="text-sm text-muted-foreground">{t.body}</Markdown>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ favorites: 0, documents: 0, conversations: 0 });
  const [recentDocs, setRecentDocs] = useState([]);

  const variant = VARIANTS[user?.persona] || VARIANTS.undergraduate;
  const texts = useSiteTexts(user?.persona);
  const PersonaIcon = variant.icon;

  useEffect(() => {
    Promise.all([
      api.get("/favorites").catch(() => ({ data: [] })),
      api.get("/tools/documents").catch(() => ({ data: [] })),
      api.get("/chat/conversations/count").catch(() => ({ data: { total: 0 } })),
    ]).then(([f, d, c]) => {
      setStats({ favorites: f.data.length, documents: d.data.length, conversations: c.data.total });
      setRecentDocs(d.data.slice(0, 4));
    });
  }, []);

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto px-5 py-8">
        {/* hero */}
        <div className="relative overflow-hidden rounded-3xl bg-navy text-white p-8 mb-8">
          <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-orange/20 blur-2xl" />
          <div className="relative flex items-start justify-between">
            <div>
              <div className="eyebrow text-xs text-orange mb-2 flex items-center gap-2">
                <PersonaIcon className="h-4 w-4" /> {user?.persona} dashboard
              </div>
              <h1 className="headline text-4xl sm:text-5xl">Welcome, {user?.name?.split(" ")[0]}</h1>
              <p className="text-white/70 mt-2 max-w-lg">{variant.intro}</p>
            </div>
            <Button data-testid="dash-chat-btn" onClick={() => navigate("/agent")} className="hidden sm:inline-flex rounded-full bg-orange hover:bg-orange-dark text-white font-display font-bold italic uppercase text-xs tracking-wide">
              <Sparkles className="h-4 w-4 mr-1.5" /> Ask advisor
            </Button>
          </div>
          <div className="relative grid grid-cols-3 gap-4 mt-8 max-w-md">
            {[
              { l: "Favorites", v: stats.favorites, icon: Heart },
              { l: "Documents", v: stats.documents, icon: FolderOpen },
              { l: "Chats", v: stats.conversations, icon: MessagesSquare },
            ].map((s) => (
              <div key={s.l} className="bg-white/10 rounded-2xl px-4 py-3">
                <div className="font-display font-black italic text-2xl text-orange">{s.v}</div>
                <div className="text-xs text-white/60">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        <PlatformIntro persona={user?.persona} />

        {/* priority actions */}
        <div className="eyebrow text-xs text-orange mb-3 flex items-center gap-2"><TrendingUp className="h-4 w-4" /> {variant.headline}</div>
        <div className="grid sm:grid-cols-3 gap-5 mb-10">
          {variant.priority.map((p) => {
            const Icon = p.icon;
            const orange = p.tone === "orange";
            return (
              <button
                key={p.title}
                data-testid={`dash-priority-${p.to.split("/").pop()}`}
                onClick={() => navigate(p.to)}
                className={`text-left p-6 rounded-2xl border shadow-card transition-all hover:-translate-y-1 hover:shadow-pop ${orange ? "bg-orange text-white border-orange" : "bg-white border-border"}`}
              >
                <div className={`h-12 w-12 rounded-xl flex items-center justify-center mb-4 ${orange ? "bg-white/20" : "bg-orange/15"}`}>
                  <Icon className={`h-6 w-6 ${orange ? "text-white" : "text-orange"}`} />
                </div>
                <h3 className={`font-display font-extrabold italic text-lg ${orange ? "text-white" : "text-navy"}`}>{p.title}</h3>
                <p className={`text-sm mt-1 ${orange ? "text-white/80" : "text-muted-foreground"}`}>{p.desc}</p>
                <div className={`mt-4 inline-flex items-center text-xs font-bold uppercase tracking-wide ${orange ? "text-white" : "text-orange"}`}>
                  Open <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
                </div>
              </button>
            );
          })}
        </div>

        {/* all tools + recent docs */}
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="eyebrow text-xs text-orange mb-3">All tools</div>
            <div className="grid sm:grid-cols-2 gap-4">
              {ALL_TOOLS.map((t) => {
                const Icon = t.icon;
                const info = texts?.[TOOL_KEYS[t.to]];
                return (
                  <button key={t.title} data-testid={`dash-tool-${t.to.split("/").pop()}`} onClick={() => navigate(t.to)}
                    className="group text-left flex flex-col gap-3 p-5 rounded-2xl bg-white border border-border hover:border-orange hover:shadow-card transition-[border-color,box-shadow]">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-xl bg-navy/5 flex items-center justify-center shrink-0"><Icon className="h-5 w-5 text-navy" /></div>
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-navy">{t.title}</div>
                        {info && <div className="text-xs font-semibold text-orange truncate">{info.title}</div>}
                      </div>
                      <ArrowUpRight className="h-4 w-4 ml-auto text-muted-foreground group-hover:text-orange shrink-0" />
                    </div>
                    {info && (
                      <div className="[&_strong]:text-navy [&_p]:mb-1.5 [&_ul]:list-disc [&_ul]:pl-4 [&_li]:my-0.5" data-testid={`dash-tool-desc-${TOOL_KEYS[t.to]}`}>
                        <Markdown className="text-xs leading-relaxed text-muted-foreground">{info.body}</Markdown>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="eyebrow text-xs text-orange mb-3">Recent documents</div>
            <div className="bg-white rounded-2xl border border-border p-2">
              {recentDocs.length === 0 ? (
                <p className="text-sm text-muted-foreground p-4 text-center">No documents yet. Generate your first one!</p>
              ) : (
                recentDocs.map((d) => (
                  <button key={d.id} onClick={() => navigate("/documents")} className="w-full text-left flex items-center gap-3 p-3 rounded-xl hover:bg-secondary transition-colors">
                    <FolderOpen className="h-4 w-4 text-orange shrink-0" />
                    <span className="text-sm font-medium text-navy truncate">{d.title}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
